import React, { useMemo, useRef, useState, Suspense } from "react";
import { useFrame } from "@react-three/fiber";
import { Line, Billboard, Text } from "@react-three/drei";
import * as THREE from "three";
import { latLonToVec3, trackToPoints, GOLD, NODES, CITIES, breadcrumbAt, readoutsFor } from "../lib/storm";

// Warm-core hurricane: symmetric ring of convective eyewall towers + heat-content patch
function WarmCoreNode({ position, sim, forensic, thermal }) {
  const group = useRef();
  const hazeRef = useRef();
  const coreRef = useRef();
  const towers = useMemo(() => new Array(12).fill(0).map((_, i) => (i / 12) * Math.PI * 2), []);
  useFrame((s) => {
    if (group.current) group.current.rotation.y = s.clock.elapsedTime * 0.6;
    // Heat-haze: expanding, fading ring symbolising latent heat rising from the warm sea
    if (hazeRef.current) {
      const p = (s.clock.elapsedTime * 0.6) % 1;
      const sc = 0.6 + p * 0.9;
      hazeRef.current.scale.set(sc, sc, sc);
      if (hazeRef.current.material) hazeRef.current.material.opacity = (1 - p) * 0.85;
    }
    // Core pulse (breathing heat)
    if (coreRef.current) {
      const b = 1 + Math.sin(s.clock.elapsedTime * 3.2) * 0.18;
      coreRef.current.scale.set(b, b, b);
    }
  });
  const scale = 0.5 + (sim.current.intensity / 5) * 0.8;
  return (
    <group position={position}>
      {/* ocean heat content patch (>28C) — forensic view only */}
      {forensic && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.001, 0]}>
          <circleGeometry args={[0.14, 32]} />
          <meshBasicMaterial color="#ff5a2b" transparent opacity={0.32} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
      )}
      {/* THERMAL: bright warm-core RED heat markers — raised above the cloud deck, additive glow */}
      {thermal && (
        <group>
          {/* broad heat field on the sea surface */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.05, 0]} renderOrder={20}>
            <circleGeometry args={[0.24, 48]} />
            <meshBasicMaterial color="#ff2f10" transparent opacity={0.55} blending={THREE.AdditiveBlending} depthWrite={false} depthTest={false} toneMapped={false} side={THREE.DoubleSide} />
          </mesh>
          {/* rising heat-haze pulse ring */}
          <mesh ref={hazeRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.07, 0]} renderOrder={21}>
            <ringGeometry args={[0.14, 0.2, 48]} />
            <meshBasicMaterial color="#ff7a1e" transparent opacity={0.8} blending={THREE.AdditiveBlending} depthWrite={false} depthTest={false} toneMapped={false} side={THREE.DoubleSide} />
          </mesh>
          {/* hot inner gradient */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.08, 0]} renderOrder={22}>
            <circleGeometry args={[0.12, 40]} />
            <meshBasicMaterial color="#ffab24" transparent opacity={0.85} blending={THREE.AdditiveBlending} depthWrite={false} depthTest={false} toneMapped={false} side={THREE.DoubleSide} />
          </mesh>
          {/* blazing eye core */}
          <mesh ref={coreRef} position={[0, 0.1, 0]} renderOrder={23}>
            <sphereGeometry args={[0.05, 20, 20]} />
            <meshBasicMaterial color="#fff1a0" transparent opacity={1} blending={THREE.AdditiveBlending} depthWrite={false} depthTest={false} toneMapped={false} />
          </mesh>
        </group>
      )}
      <group ref={group} scale={[scale, scale, scale]}>
        {towers.map((a, i) => (
          <mesh key={i} position={[Math.cos(a) * 0.05, 0.04, Math.sin(a) * 0.05]}>
            <coneGeometry args={[0.012, 0.09, 8]} />
            <meshStandardMaterial color="#ffffff" emissive="#dfe6f0" emissiveIntensity={0.35} roughness={0.8} />
          </mesh>
        ))}
        <mesh position={[0, 0.01, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 0.01, 24]} />
          <meshBasicMaterial color={forensic ? "#0a1a2a" : "#c9d3df"} />
        </mesh>
      </group>
    </group>
  );
}

// Cold-core nor'easter: asymmetric comma head + clashing warm/cold frontal boundaries
function ColdCoreNode({ position, sim, forensic, thermal }) {
  const group = useRef();
  useFrame((s) => {
    if (group.current) group.current.rotation.y = s.clock.elapsedTime * 0.4;
  });
  const scale = 0.5 + (sim.current.intensity / 5) * 0.7;
  const comma = useMemo(
    () => new Array(9).fill(0).map((_, i) => {
      const a = (i / 9) * Math.PI * 1.4;
      const r = 0.03 + i * 0.006;
      return [Math.cos(a) * r, 0.03 + i * 0.004, Math.sin(a) * r];
    }),
    []
  );
  const coldFront = useMemo(() => [[-0.16, 0.02, 0.06], [-0.04, 0.03, -0.02], [0.1, 0.04, -0.12]], []);
  const warmFront = useMemo(() => [[-0.14, 0.02, 0.02], [0.0, 0.02, 0.08], [0.16, 0.02, 0.16]], []);
  return (
    <group position={position}>
      {/* THERMAL: cold-core blue center + clashing warm (red) & cold (blue) fronts */}
      {thermal && (
        <>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.001, 0]}>
            <circleGeometry args={[0.18, 48]} />
            <meshBasicMaterial color="#1f5cff" transparent opacity={0.3} depthWrite={false} side={THREE.DoubleSide} />
          </mesh>
          <mesh position={[0, 0.02, 0]}>
            <sphereGeometry args={[0.026, 18, 18]} />
            <meshBasicMaterial color="#8fc0ff" transparent opacity={0.85} />
          </mesh>
          <Line points={coldFront} color="#4d8bff" lineWidth={3} />
          <Line points={warmFront} color="#ff5a5a" lineWidth={3} />
        </>
      )}
      <group ref={group} scale={[scale, scale, scale]}>
        {comma.map((pos, i) => (
          <mesh key={i} position={pos}>
            <boxGeometry args={[0.02, 0.05, 0.02]} />
            <meshStandardMaterial color="#ffffff" emissive="#e6eefc" emissiveIntensity={0.3} roughness={0.85} />
          </mesh>
        ))}
      </group>
      {/* frontal boundary + jet-stream trough — forensic view only */}
      {forensic && (
        <>
          <Line points={[[-0.16, 0.01, 0.05], [-0.02, 0.02, -0.04], [0.14, 0.03, -0.12]]} color="#5aa9ff" lineWidth={2} />
          <Line points={[[-0.2, 0.09, -0.12], [0, 0.13, 0.02], [0.2, 0.09, 0.14]]} color={GOLD} lineWidth={1.5} dashed dashSize={0.02} gapSize={0.02} />
        </>
      )}
    </group>
  );
}

function CityPin({ city, radius, onSelect }) {
  const ref = useRef();
  const base = useMemo(() => latLonToVec3(city.lat, city.lon, radius), [city, radius]);
  const outer = useMemo(() => latLonToVec3(city.lat, city.lon, radius + 0.05), [city, radius]);
  const dir = useMemo(() => base.clone().normalize(), [base]);
  const quat = useMemo(() => {
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    return q;
  }, [dir]);
  useFrame((s) => {
    if (ref.current) ref.current.scale.setScalar(1 + Math.sin(s.clock.elapsedTime * 3) * 0.12);
  });
  const SILVER = "#dfe6ee";
  return (
    <group>
      <Line points={[base, outer]} color={SILVER} lineWidth={1.5} transparent opacity={0.8} />
      <group position={outer} quaternion={quat} ref={ref}
        onClick={(e) => { e.stopPropagation(); onSelect(city); }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { document.body.style.cursor = "auto"; }}
        data-testid={`globe-city-${city.id}`}>
        <mesh position={[0, 0.03, 0]}>
          <sphereGeometry args={[0.018, 20, 20]} />
          <meshStandardMaterial color={SILVER} emissive="#9fb0c4" emissiveIntensity={0.5} metalness={0.7} roughness={0.25} />
        </mesh>
        <mesh position={[0, 0.012, 0]}>
          <coneGeometry args={[0.01, 0.03, 12]} />
          <meshStandardMaterial color={SILVER} metalness={0.7} roughness={0.3} />
        </mesh>
      </group>
      <Suspense fallback={null}>
        <Billboard position={outer.clone().multiplyScalar(1.08)}>
          <Text font="/fonts/Cinzel.ttf" fontSize={0.03} color="#eef3fb" anchorX="center" anchorY="bottom" outlineWidth={0.001} outlineColor="#000">
            {city.name}
          </Text>
        </Billboard>
      </Suspense>
    </group>
  );
}

function Breadcrumb({ pos, label, show }) {
  const ref = useRef();
  const [hover, setHover] = useState(false);
  const active = hover || show;
  useFrame((s) => { if (ref.current) ref.current.scale.setScalar((show ? 1.5 : 1) + Math.sin(s.clock.elapsedTime * 4 + pos.x * 3) * 0.1); });
  return (
    <group position={pos}>
      <mesh ref={ref}
        onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = "auto"; }}
        data-testid="track-breadcrumb">
        <sphereGeometry args={[0.011, 12, 12]} />
        <meshStandardMaterial color={GOLD} emissive={GOLD} emissiveIntensity={show ? 1.1 : 0.7} metalness={0.6} roughness={0.3} />
      </mesh>
      {active && (
        <Suspense fallback={null}>
          <Billboard position={[0, 0.04, 0]}>
            <mesh><planeGeometry args={[0.12, 0.035]} /><meshBasicMaterial color="#0a0e1a" transparent opacity={0.85} depthTest={false} /></mesh>
            <Text position={[0, 0, 0.001]} fontSize={0.016} color="#ffe9a8" anchorX="center" anchorY="middle" outlineWidth={0.001} outlineColor="#000">{label}</Text>
          </Billboard>
        </Suspense>
      )}
    </group>
  );
}

export default function StormNodes({ sim, radius, activeNode, stormPos, eventTrack, event, onSelectCity, viewMode, timeline = 0 }) {
  const forensic = viewMode === "forensic";
  const thermal = viewMode === "thermal";
  const polo = stormPos.hurricane_polo || NODES.hurricane_polo;
  const nor = stormPos.noreaster || NODES.noreaster;
  const poloVec = useMemo(() => latLonToVec3(polo.lat, polo.lon, radius + 0.02), [polo, radius]);
  const norVec = useMemo(() => latLonToVec3(nor.lat, nor.lon, radius + 0.02), [nor, radius]);
  const eyeVec = activeNode === "hurricane_polo" ? poloVec : norVec;
  const eye = readoutsFor(sim.current.intensity);
  const eyeLabel = `${eye.level > 0 ? "C" + eye.level : "TS"} · ${eye.wind} mph`;

  const orient = (vec) => {
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), vec.clone().normalize());
    return q;
  };

  const trackPts = useMemo(
    () => (eventTrack ? trackToPoints(eventTrack, radius + 0.015) : null),
    [eventTrack, radius]
  );
  const poloRibbon = useMemo(() => trackToPoints(NODES.hurricane_polo.track, radius + 0.012), [radius]);
  const norRibbon = useMemo(() => trackToPoints(NODES.noreaster.track, radius + 0.012), [radius]);
  const landfallVec = useMemo(() => latLonToVec3(16.9, -99.9, radius + 0.02), [radius]);

  return (
    <group>
      <group position={poloVec} quaternion={orient(poloVec)}>
        <WarmCoreNode position={[0, 0, 0]} sim={sim} forensic={forensic} thermal={thermal} />
      </group>
      <group position={norVec} quaternion={orient(norVec)}>
        <ColdCoreNode position={[0, 0, 0]} sim={sim} forensic={forensic} thermal={thermal} />
      </group>

      {/* Live eye readout — category + wind at the storm's current position, synced to the timeline */}
      <group position={eyeVec} quaternion={orient(eyeVec)}>
        <Suspense fallback={null}>
          <Billboard position={[0, 0.13, 0]}>
            <mesh renderOrder={30}><planeGeometry args={[0.17, 0.052]} /><meshBasicMaterial color="#0a0e1a" transparent opacity={0.82} depthTest={false} /></mesh>
            <Text position={[0, 0, 0.001]} fontSize={0.023} color="#ffe9a8" anchorX="center" anchorY="middle" outlineWidth={0.001} outlineColor="#000" renderOrder={31} data-testid="eye-readout">{eyeLabel}</Text>
          </Billboard>
        </Suspense>
      </group>

      {CITIES.map((c) => (
        <CityPin key={c.id} city={c} radius={radius} onSelect={onSelectCity} />
      ))}

      {/* Live storm-node History Trajectory ribbons: GOLD hurricane, BLUE nor'easter */}
      <Line points={poloRibbon} color={GOLD} lineWidth={6} transparent opacity={0.18} />
      <Line points={poloRibbon} color="#ffe9a8" lineWidth={2} transparent opacity={0.85} />
      <Line points={norRibbon} color="#2f6bff" lineWidth={6} transparent opacity={0.2} />
      <Line points={norRibbon} color="#8fc0ff" lineWidth={2} transparent opacity={0.9} />

      {/* Gold LANDFALL marker where Hurricane Polo hits Acapulco */}
      <group position={landfallVec} quaternion={orient(landfallVec)}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.006, 0]}><ringGeometry args={[0.03, 0.05, 32]} /><meshBasicMaterial color={GOLD} transparent opacity={0.9} side={THREE.DoubleSide} depthWrite={false} /></mesh>
        <mesh position={[0, 0.03, 0]}><sphereGeometry args={[0.016, 16, 16]} /><meshStandardMaterial color={GOLD} emissive={GOLD} emissiveIntensity={0.8} /></mesh>
        <Suspense fallback={null}>
          <Billboard position={[0, 0.09, 0]}><Text font="/fonts/Cinzel.ttf" fontSize={0.028} color="#ffe9a8" anchorX="center" anchorY="bottom" outlineWidth={0.001} outlineColor="#000">LANDFALL</Text></Billboard>
        </Suspense>
      </group>

      {/* Historical trajectory: glowing gold ribbon + interactive breadcrumbs */}
      {trackPts && (
        <>
          <Line points={trackPts} color={GOLD} lineWidth={8} transparent opacity={0.22} />
          <Line points={trackPts} color="#ffe9a8" lineWidth={2.6} transparent opacity={0.95} />
          {event && eventTrack.map((p, i) => {
            const frac = i / Math.max(1, eventTrack.length - 1);
            const bc = breadcrumbAt(event.peak_wind_mph, frac);
            const activeIdx = Math.round(timeline * (eventTrack.length - 1));
            return (
              <Breadcrumb key={i} pos={trackPts[i]} show={i === activeIdx}
                label={`${bc.label} · ${bc.wind} mph`} />
            );
          })}
        </>
      )}
    </group>
  );
}
